import React, { useCallback, useState } from 'react';

import { useIntl } from 'react-intl-next';

import Button from '@atlaskit/button/new';
import { CodeBlock } from '@atlaskit/code';
import { cssMap } from '@atlaskit/css';
import CopyIcon from '@atlaskit/icon/core/copy';
import SuccessIcon from '@atlaskit/icon/core/status-success';
import { Box } from '@atlaskit/primitives/compiled';
import { TabPanel } from '@atlaskit/tabs';

import { messages } from '../messages';

import { Panel } from './panel-wrapper';

const styles = cssMap({
	copyJsonButton: {
		marginLeft: 'auto',
	},
});

type RawJsonPanelProps = {
	rawJson: unknown;
};

const formatJson = (data: unknown): string => {
	return JSON.stringify(data, null, 2);
};

export const RawJsonPanel = ({ rawJson }: RawJsonPanelProps) => {
	const { formatMessage } = useIntl();
	const [jsonCopied, setJsonCopied] = useState(false);

	const copyFinalResponseJson = useCallback(async (): Promise<void> => {
		try {
			if (!rawJson) {
				return;
			}

			const jsonString = formatJson(rawJson);
			if (!jsonString) {
				return;
			}

			await navigator.clipboard.writeText(jsonString);
			setJsonCopied(true);

			setTimeout(() => {
				setJsonCopied(false);
			}, 2500);
		} catch {
			return;
		}
	}, [rawJson]);

	return (
		<TabPanel>
			<Panel>
				<Box xcss={styles.copyJsonButton}>
					<Button
						appearance="primary"
						onClick={copyFinalResponseJson}
						iconBefore={jsonCopied ? SuccessIcon : CopyIcon}
					>
						{jsonCopied
							? formatMessage(messages.ai_debugger_modal_copy_button_copied_text)
							: formatMessage(messages.ai_debugger_modal_copy_button_text)}
					</Button>
				</Box>
				<CodeBlock language="json" text={formatJson(rawJson ?? null)} shouldWrapLongLines />
			</Panel>
		</TabPanel>
	);
};
